// components/chat/chat-input/AttachmentPreview.tsx
import { X, FileText, Music, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useSignedAttachmentUrl } from "@/hooks/useSignedAttachmentUrl";

const REMOVE_BTN =
  "absolute -end-1 -top-1 inline-flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-white bg-foreground text-background shadow-sm hover:bg-foreground/90";

export interface AttachmentPreviewProps {
  file: File;
  onRemove: () => void;
  isProcessing?: boolean;
}

function getKind(file: File): "image" | "audio" | "doc" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("audio/")) return "audio";
  return "doc";
}

function getExtension(file: File): string {
  const name = file.name.toLowerCase();
  const dot = name.lastIndexOf(".");
  if (dot >= 0) return name.slice(dot + 1).toUpperCase();
  return file.type.split("/")[1]?.toUpperCase() || "FILE";
}

export const AttachmentPreview = ({ file, onRemove, isProcessing }: AttachmentPreviewProps) => {
  const kind = getKind(file);

  if (kind === "image") return <ImageAttachment file={file} onRemove={onRemove} isProcessing={isProcessing} />;
  if (kind === "audio") return <AudioAttachment file={file} onRemove={onRemove} isProcessing={isProcessing} />;
  return <FileAttachment file={file} onRemove={onRemove} isProcessing={isProcessing} />;
};

const ImageAttachment = ({ file, onRemove, isProcessing }: AttachmentPreviewProps) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  return (
    <div className="relative inline-flex h-14 w-14 items-center gap-2 rounded-lg border bg-card px-3 py-2 shadow-sm transition-all duration-200 hover:shadow-md">
      {url && (
        <img
          src={url}
          alt={file.name}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full rounded-lg object-cover p-0.5"
        />
      )}
      {isProcessing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60">
          <Loader2 className="h-4 w-4 animate-spin text-foreground" />
        </div>
      )}
      <button type="button" className={REMOVE_BTN} onClick={onRemove} aria-label="Remove attachment">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

const FileAttachment = ({ file, onRemove, isProcessing }: AttachmentPreviewProps) => (
  <div className="relative inline-flex items-center gap-2 rounded-lg border bg-card px-2 py-2 shadow-sm transition-all duration-200 hover:shadow-md">
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm bg-amber-100 dark:bg-amber-900/30">
      {isProcessing ? (
        <Loader2 className="h-4 w-4 animate-spin text-amber-600 dark:text-amber-400" />
      ) : (
        <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      )}
    </div>
    <div className="flex min-w-0 flex-col">
      <span className="max-w-[150px] truncate text-sm font-medium text-foreground">{file.name}</span>
      <span className="text-xs uppercase text-muted-foreground">{getExtension(file)}</span>
    </div>
    <button type="button" className={REMOVE_BTN} onClick={onRemove} aria-label="Remove attachment">
      <X className="h-3 w-3" />
    </button>
  </div>
);

const AudioAttachment = ({ file, onRemove, isProcessing }: AttachmentPreviewProps) => (
  <div className="relative inline-flex items-center gap-2 rounded-lg border bg-card px-2 py-2 shadow-sm transition-all duration-200 hover:shadow-md">
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm bg-violet-100 dark:bg-violet-900/30">
      {isProcessing ? (
        <Loader2 className="h-4 w-4 animate-spin text-violet-600 dark:text-violet-400" />
      ) : (
        <Music className="h-4 w-4 text-violet-600 dark:text-violet-400" />
      )}
    </div>
    <div className="flex min-w-0 flex-col">
      <span className="max-w-[150px] truncate text-sm font-medium text-foreground">{file.name}</span>
      <span className="text-xs uppercase text-muted-foreground">AUDIO</span>
    </div>
    <button type="button" className={REMOVE_BTN} onClick={onRemove} aria-label="Remove attachment">
      <X className="h-3 w-3" />
    </button>
  </div>
);

/**
 * Read-only pill used when rendering attachments saved on a sent message.
 */
export const SentAttachmentPill: React.FC<{
  name: string;
  kind: "image" | "audio" | "doc" | "pdf" | "text" | "ppt" | "other";
  url?: string;
  storagePath?: string;
}> = ({ name, kind, url, storagePath }) => {
  // For images, fall back to a re-signed URL when no fresh `url` is present
  // (e.g. on history reload after the original signed URL expired).
  const needsSign = kind === "image" && !url && !!storagePath;
  const { url: signedUrl, isLoading } = useSignedAttachmentUrl(needsSign ? storagePath : undefined);
  const resolvedUrl = url || signedUrl;

  if (kind === "image") {
    return (
      <div className="relative inline-flex h-14 w-14 items-center justify-center gap-2 rounded-lg border bg-card shadow-sm overflow-hidden">
        {resolvedUrl ? (
          <img
            src={resolvedUrl}
            alt={name}
            loading="lazy"
            className="absolute inset-0 h-full w-full rounded-lg object-cover p-0.5"
          />
        ) : isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <FileText className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    );
  }
  const isAudio = kind === "audio";
  const tile = isAudio
    ? "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400"
    : "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400";
  const Icon = isAudio ? Music : FileText;
  const ext = isAudio ? "AUDIO" : kind.toUpperCase();
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border bg-card px-2 py-2 shadow-sm max-w-full">
      <div className={cn("flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm", tile)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="max-w-[120px] sm:max-w-[180px] truncate text-sm font-medium text-foreground">{name}</span>
        <span className="text-xs uppercase text-muted-foreground">{ext}</span>
      </div>
    </div>
  );
};
